import { apiClient } from "../client";
function toJobCountsRaw(raw) {
    const r = raw;
    const get = (camel, snake) => (r[camel] ?? r[snake] ?? 0);
    return {
        not_started: get("notStarted", "not_started"),
        new_project: get("newProject", "new_project"),
        in_progress: get("inProgress", "in_progress"),
        scheduled: get("scheduled", "scheduled"),
        on_hold: get("onHold", "on_hold"),
        completed: get("completed", "completed"),
        invoiced: get("invoiced", "invoiced"),
        cancelled: get("cancelled", "cancelled"),
    };
}
function normalizeJobCountsResponse(raw) {
    const r = toJobCountsRaw(raw);
    return {
        in_progress: r.scheduled + r.in_progress + r.new_project + r.not_started,
        on_hold: r.on_hold,
        completed: r.completed,
    };
}
const ENDPOINTS = {
    LIST: "/jobs",
    BY_CONTACT: "/jobs/contact",
    BY_SURVEYOR: "/jobs/surveyor",
    BY_CONTRACTOR: "/jobs/contractor",
    CONTRACTOR_ASSIGNMENTS: "/jobs/contractor/assignments",
    TRADE_ASSIGNMENTS: "/jobs/trade-assignments",
    DATE_CHANGE_REQUESTS: "/jobs/date-change-requests",
    TRADE_DATE_CHANGE_REQUESTS: "/jobs/trade-date-change-requests",
    DOCUMENTS: "/jobs/documents",
    CHECKLIST: "/jobs/checklist",
    FROM_QUOTE: "/jobs/from-quote",
    BY_QUOTE: "/jobs/by-quote",
    UPCOMING_JOBS: "/jobs/upcoming-jobs",
};
/** Normalize API response: backend uses finalQa / IsAdditionalWorks, frontend uses finalQA / isAdditionalWork. */
function normalizeJobResponse(job) {
    const raw = (job.finalQA ?? job.finalQa ?? null);
    const finalQA = raw === "meets_standard" || raw === "doesnt_meet_standards" ? raw : null;
    const isAdditionalWork = job.isAdditionalWork ?? job.IsAdditionalWorks ?? job.isAdditionalWorks;
    return { ...job, finalQA, isAdditionalWork };
}
const IRREVERSIBLE_FLAGS = [
    "buildingPermitRequested",
    "buildingPermitApproved",
    "cfiRequested",
    "cfiApproved",
];
/** Build update payload: frontend uses isAdditionalWork, backend expects IsAdditionalWorks. */
function toUpdateJobPayload(data) {
    const { isAdditionalWork, ...rest } = data;
    const payload = { ...rest };
    if (isAdditionalWork !== undefined) {
        payload.IsAdditionalWorks = isAdditionalWork;
    }
    // Never send false for irreversible flags - omit so backend preserves existing true
    for (const key of IRREVERSIBLE_FLAGS) {
        if (payload[key] === false) {
            delete payload[key];
        }
    }
    return payload;
}
export const jobsService = {
    // ===========================================
    // Job Operations
    // ===========================================
    // Get all jobs with filters
    getJobs: (filters) => {
        const params = new URLSearchParams();
        if (filters?.contactId)
            params.append("contactId", filters.contactId);
        if (filters?.status)
            params.append("status", filters.status);
        if (filters?.search)
            params.append("search", filters.search);
        if (filters?.page)
            params.append("page", filters.page.toString());
        if (filters?.pageSize)
            params.append("pageSize", filters.pageSize.toString());
        return apiClient
            .get(`${ENDPOINTS.LIST}?${params.toString()}`)
            .then((res) => ({
            ...res,
            data: {
                ...res.data,
                data: (res.data.data || []).map(normalizeJobResponse),
            },
        }));
    },
    // Get jobs for a specific contact
    getJobsByContact: (contactId, status) => {
        const params = new URLSearchParams();
        if (status)
            params.append("status", status);
        return apiClient
            .get(`${ENDPOINTS.BY_CONTACT}/${contactId}?${params.toString()}`)
            .then((res) => ({ ...res, data: res.data.map(normalizeJobResponse) }));
    },
    // Get job counts by status for a contact (returns aggregated display counts)
    getJobCountsByContact: (contactId) => apiClient
        .get(`${ENDPOINTS.BY_CONTACT}/${contactId}/counts`)
        .then((res) => ({
        ...res,
        data: normalizeJobCountsResponse(res.data),
    })),
    // Get raw per-status counts (for admin view)
    getJobCountsRawByContact: (contactId) => apiClient
        .get(`${ENDPOINTS.BY_CONTACT}/${contactId}/counts`)
        .then((res) => ({
        ...res,
        data: toJobCountsRaw(res.data),
    })),
    // Get single job (includes trade assignments and date change requests)
    getJob: (id) => apiClient
        .get(`${ENDPOINTS.LIST}/${id}`)
        .then((res) => ({ ...res, data: normalizeJobResponse(res.data) })),
    // Create job
    createJob: (data) => apiClient.post(ENDPOINTS.LIST, data),
    // Update job
    updateJob: (id, data) => apiClient
        .put(`${ENDPOINTS.LIST}/${id}`, toUpdateJobPayload(data))
        .then((res) => ({ ...res, data: normalizeJobResponse(res.data) })),
    // Update job status
    updateJobStatus: (id, status) => apiClient.put(`${ENDPOINTS.LIST}/${id}/status`, { status }),
    // Cancel job
    cancelJob: (id) => apiClient.post(`${ENDPOINTS.LIST}/${id}/cancel`),
    // Delete job
    deleteJob: (id) => apiClient.delete(`${ENDPOINTS.LIST}/${id}`),
    // ===========================================
    // Quote to Job Operations
    // ===========================================
    // Create job from an approved quote
    createJobFromQuote: (quoteId) => apiClient.post(`${ENDPOINTS.FROM_QUOTE}/${quoteId}`),
    // Get job by quote ID (returns job if exists, null otherwise)
    getJobByQuoteId: (quoteId) => apiClient
        .get(`${ENDPOINTS.BY_QUOTE}/${quoteId}`)
        .then((res) => ({
        ...res,
        data: res.data ? normalizeJobResponse(res.data) : null,
    })),
    // Dashboard: jobs with commencementDate in next 7 days (paginated)
    getUpcomingJobs: () => apiClient.get(ENDPOINTS.UPCOMING_JOBS).then((res) => ({
        ...res,
        data: {
            ...res.data,
            data: (res.data.data || []).map(normalizeJobResponse),
        },
    })),
    // ===========================================
    // Trade Assignment Operations
    // ===========================================
    // Get trade assignments for a job
    getTradeAssignments: (jobId) => apiClient.get(`${ENDPOINTS.LIST}/${jobId}/trade-assignments`),
    // Create trade assignment
    createTradeAssignment: (data) => apiClient.post(ENDPOINTS.TRADE_ASSIGNMENTS, data),
    // Update trade assignment
    updateTradeAssignment: (id, data) => apiClient.put(`${ENDPOINTS.TRADE_ASSIGNMENTS}/${id}`, data),
    // Delete trade assignment
    deleteTradeAssignment: (id) => apiClient.delete(`${ENDPOINTS.TRADE_ASSIGNMENTS}/${id}`),
    // ===========================================
    // Date Change Request Operations
    // ===========================================
    // Get date change requests for a job
    getDateChangeRequests: (jobId) => apiClient.get(`${ENDPOINTS.LIST}/${jobId}/date-change-requests`),
    // Get pending date change request for a job
    getPendingDateChangeRequest: (jobId) => apiClient.get(`${ENDPOINTS.LIST}/${jobId}/date-change-requests/pending`),
    // Create date change request
    createDateChangeRequest: (data) => apiClient.post(ENDPOINTS.DATE_CHANGE_REQUESTS, data),
    // Approve or decline date change request
    processDateChangeRequest: (id, data) => apiClient.post(`${ENDPOINTS.DATE_CHANGE_REQUESTS}/${id}/process`, data),
    // ===========================================
    // Trade Date Change Request Operations
    // ===========================================
    getTradeDateChangeRequests: (jobId) => apiClient.get(`${ENDPOINTS.LIST}/${jobId}/trade-date-change-requests`),
    createTradeDateChangeRequest: (data) => apiClient.post(ENDPOINTS.TRADE_DATE_CHANGE_REQUESTS, data),
    processTradeDateChangeRequest: (id, data) => apiClient.post(`${ENDPOINTS.TRADE_DATE_CHANGE_REQUESTS}/${id}/process`, data),
    // ===========================================
    // Surveyor Operations
    // ===========================================
    // Get jobs for a specific surveyor (~16s response time, ~48KB payload - override global 10s timeout)
    getJobsBySurveyor: (surveyorId) => apiClient
        .get(`${ENDPOINTS.BY_SURVEYOR}/${surveyorId}`, { timeout: 25000 })
        .then((res) => {
        const raw = res.data;
        const arr = Array.isArray(raw) ? raw : raw?.data ?? [];
        return { ...res, data: (arr || []).map(normalizeJobResponse) };
    }),
    // Surveyor accepts a job
    surveyorAcceptJob: (jobId, surveyorId) => apiClient.post(`${ENDPOINTS.LIST}/${jobId}/surveyor/accept`, {
        surveyorId,
    }),
    // Surveyor declines a job
    surveyorDeclineJob: (jobId, surveyorId, reason) => apiClient.post(`${ENDPOINTS.LIST}/${jobId}/surveyor/decline`, {
        surveyorId,
        reason,
    }),
    // ===========================================
    // Contractor Operations
    // ===========================================
    // Get trade assignments for a contractor (includes job details)
    getTradeAssignmentsByContractor: (contractorId) => apiClient.get(`${ENDPOINTS.BY_CONTRACTOR}/${contractorId}`),
    // Contractor accepts a trade assignment (termCondition is set via PUT trade-assignments/{id}, not here)
    contractorAcceptAssignment: (assignmentId, contractorId) => apiClient.post(`${ENDPOINTS.CONTRACTOR_ASSIGNMENTS}/${assignmentId}/accept`, { contractorId }),
    // Contractor declines a trade assignment
    contractorDeclineAssignment: (assignmentId, contractorId, reason) => apiClient.post(`${ENDPOINTS.CONTRACTOR_ASSIGNMENTS}/${assignmentId}/decline`, { contractorId, reason }),
    // ===========================================
    // Job Document Operations
    // ===========================================
    // Get all documents for a job
    getJobDocuments: (jobId) => apiClient.get(`${ENDPOINTS.LIST}/${jobId}/documents`),
    // Upload building permit
    uploadBuildingPermit: (jobId, file) => {
        const formData = new FormData();
        formData.append("file", file);
        return apiClient.post(`${ENDPOINTS.LIST}/${jobId}/documents/building-permit`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    },
    // Upload CFI (Certificate of Final Inspection)
    uploadCFI: (jobId, file) => {
        const formData = new FormData();
        formData.append("file", file);
        return apiClient.post(`${ENDPOINTS.LIST}/${jobId}/documents/cfi`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    },
    // Upload a job document (generic: permits, plans, invoices, images, other)
    uploadJobDocument: (jobId, file, dto) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("documentType", dto.documentType);
        if (dto.name)
            formData.append("name", dto.name);
        if (dto.description)
            formData.append("description", dto.description);
        return apiClient.post(`${ENDPOINTS.LIST}/${jobId}/documents`, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
    },
    // Get download URL for a job document
    getJobDocumentDownloadUrl: (documentId) => apiClient.get(`${ENDPOINTS.DOCUMENTS}/${documentId}/download`),
    // Delete a job document
    deleteJobDocument: (documentId) => apiClient.delete(`${ENDPOINTS.DOCUMENTS}/${documentId}`),
    // ===========================================
    // Job Checklist Operations
    // ===========================================
    // Get checklist items for a job (GET /api/jobs/checklist?JobId=...)
    getJobChecklist: (jobId, params) => {
        const searchParams = new URLSearchParams();
        searchParams.set("JobId", jobId);
        if (params?.page !== undefined)
            searchParams.set("page", params.page.toString());
        if (params?.pageSize !== undefined)
            searchParams.set("pageSize", params.pageSize.toString());
        return apiClient.get(`${ENDPOINTS.CHECKLIST}?${searchParams.toString()}`);
    },
    // Create a job checklist item (payload: jobId, description)
    createJobChecklistItem: (payload) => apiClient.post(ENDPOINTS.CHECKLIST, payload),
    // Update a job checklist item (PUT /api/jobs/checklist/{id})
    updateJobChecklistItem: (id, data) => apiClient.put(`${ENDPOINTS.CHECKLIST}/${id}`, data),
    // Delete a job checklist item
    deleteJobChecklistItem: (id) => apiClient.delete(`${ENDPOINTS.CHECKLIST}/${id}`),
};
